import { useState } from "react";
import { Translation } from "../types/api/common";
import { LanguageResponse } from "../types/api/lang";

export const useTranslations = (initialTranslations: Translation[] = []) => {
  const [translations, setTranslations] =
    useState<Translation[]>(initialTranslations);
  const [langs, setLangs] = useState<LanguageResponse[]>([]);
  const [currentLangIndex, setCurrentLangIndex] = useState<number>(0);

  const langsDidUpdated = (langs: LanguageResponse[]) => {
    const sortedLangs = [...langs].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateA - dateB;
    });

    setLangs(sortedLangs);

    const activeLangCodes = sortedLangs.map((lang) => lang.isoCode);
    setTranslations((prev) =>
      prev.filter((translation) =>
        activeLangCodes.includes(translation.languageIsoCode)
      )
    );

    if (sortedLangs.length <= currentLangIndex) {
      setCurrentLangIndex(0);
    }
  };

  // Метод для получения текущего значения поля
  const getCurrentFieldValue = (fieldName: string): string => {
    const currentLang = langs[currentLangIndex];
    if (!currentLang) return "";

    const translation = translations.find(
      (t) => t.languageIsoCode === currentLang.isoCode
    );
    const field = translation?.fields.find((f) => f.fieldName === fieldName);
    return field?.value || "";
  };

  // Метод для обновления значения поля
  const updateFieldValue = (fieldName: string, value: string) => {
    const currentLang = langs[currentLangIndex];
    if (!currentLang) return;

    setTranslations((prev) => {
      const updated = prev.map((translation) => ({
        ...translation,
        fields: translation.fields.map((field) => ({ ...field })), // глубокая копия
      }));

      const translationIndex = updated.findIndex(
        (t) => t.languageIsoCode === currentLang.isoCode
      );

      if (translationIndex >= 0) {
        const fieldIndex = updated[translationIndex].fields.findIndex(
          (f) => f.fieldName === fieldName
        );
        if (fieldIndex >= 0) {
          updated[translationIndex].fields[fieldIndex] = {
            ...updated[translationIndex].fields[fieldIndex],
            value,
          };
        } else {
          updated[translationIndex].fields.push({ fieldName, value });
        }
      } else {
        updated.push({
          languageIsoCode: currentLang.isoCode,
          fields: [{ fieldName, value }],
        });
      }

      return updated;
    });
  };

  const getRussianOrFirstFieldValue = (fieldName: string): string => {
    const russianTranslation = translations.find(
      (t) => t.languageIsoCode === "ru"
    );
    const russianField = russianTranslation?.fields.find(
      (f) => f.fieldName === fieldName
    );

    if (russianField?.value?.trim()) {
      return russianField.value;
    }

    // Если русского нет или пустой, берем первый из массива
    const firstTranslation = translations[0];
    const firstField = firstTranslation?.fields.find(
      (f) => f.fieldName === fieldName
    );

    return firstField?.value || "";
  };

  // Метод для проверки заполненности всех языков
  const isAllLanguagesFilled = (fieldName: string): boolean => {
    return langs.every((lang) => {
      const translation = translations.find(
        (t) => t.languageIsoCode === lang.isoCode
      );
      const field = translation?.fields.find((f) => f.fieldName === fieldName);
      return field?.value?.trim();
    });
  };

  // Очистить переводы для удаленных языков
  const cleanupTranslations = (activeLangCodes: string[]) => {
    setTranslations((prev) =>
      prev.filter((translation) =>
        activeLangCodes.includes(translation.languageIsoCode)
      )
    );
  };

  // Сравнение изменений
  const hasTranslationChanges = (
    originalTranslations: Translation[]
  ): boolean => {
    const editedTranslations = translations;

    if (editedTranslations.length !== originalTranslations.length) {
      return true;
    }

    for (const editedTranslation of editedTranslations) {
      const originalTranslation = originalTranslations.find(
        (t) => t.languageIsoCode === editedTranslation.languageIsoCode
      );

      if (!originalTranslation) {
        return true;
      }

      for (const editedField of editedTranslation.fields) {
        const originalField = originalTranslation.fields.find(
          (f) => f.fieldName === editedField.fieldName
        );

        if (!originalField) {
          return true;
        }

        if (editedField.value.trim() !== originalField.value.trim()) {
          return true;
        }
      }
    }

    return false;
  };

  return {
    translations,
    setTranslations,
    langs,
    setLangs,
    currentLangIndex,
    setCurrentLangIndex,
    getCurrentFieldValue,
    updateFieldValue,
    isAllLanguagesFilled,
    getRussianOrFirstFieldValue,
    cleanupTranslations,
    langsDidUpdated,
    hasTranslationChanges,
  };
};
